"use client";

import { useSeriesBookmarks } from "@/hooks/use-bookmarks";
import { useSession } from "@/lib/auth/client";
import { PageSearchParams } from "@/types";
import { Videos } from "@prisma/client";
import { useState } from "react";
import useSWR from "swr";

const fetcher = (url: string) => fetch(url).then((res) => res.json());

export type SeriesDetailParams = {
    params: { slug: string };
    searchParams: PageSearchParams;
};

export function useSeriesDetail({ params, searchParams }: SeriesDetailParams) {
    const { data, isLoading, mutate } = useSWR(
        `/api/series/video/${params.slug}`,
        fetcher,
    );

    const { data: session } = useSession();
    const isPremium = session?.user?.isPremium;

    const series = data;

    const { isBooked, toggleBookmark } = useSeriesBookmarks();
    const videoId = searchParams["videoId"];
    const timeStamp = searchParams["timeStamp"];
    const selectionKey = `${params.slug}:${Array.isArray(videoId) ? videoId.join(",") : (videoId ?? "")}`;
    const [selectedVideo, setSelectedVideo] = useState<{
        key: string;
        id: number;
    } | null>(null);
    const [autoPlay, setAutoPlay] = useState<boolean>(false);
    const [modalToggle, setModalToggle] = useState<boolean>(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const selectedId =
        selectedVideo?.key === selectionKey ? selectedVideo.id : undefined;
    const requestedId = Array.isArray(videoId)
        ? Number(videoId[0])
        : Number(videoId);
    const activeId =
        selectedId ?? (Number.isNaN(requestedId) ? undefined : requestedId);
    const currentVideo =
        series && session?.user
            ? activeId
                ? (series.Videos?.find(
                      (video: Videos) => video.Id === activeId,
                  ) ?? null)
                : (series.Videos?.[0] ?? null)
            : null;

    const handleSelection = async (video: Videos) => {
        if (!session?.user) {
            setIsLoginModalOpen(true);
            return;
        }

        if ((video.SeqNo && video.SeqNo <= 2) || isPremium) {
            setSelectedVideo({ key: selectionKey, id: video.Id });
        }
    };

    const handleLoginSuccess = () => {
        void mutate();
    };

    const handleEnd = () => {
        if (currentVideo?.SeqNo && series?.Videos) {
            const nextVideo = series.Videos.find(
                (video: Videos) =>
                    video.SeqNo === (currentVideo?.SeqNo ?? 0) + 1,
            );
            if (!nextVideo) return;
            if ((nextVideo.SeqNo <= 2 && session?.user) || isPremium) {
                setAutoPlay(true);
                setSelectedVideo({ key: selectionKey, id: nextVideo.Id });
                return;
            }
            setAutoPlay(false);
            setModalToggle(true);
        }
    };

    return {
        session,
        isPremium,
        series,
        isLoading,
        isBooked,
        toggleBookmark,
        timeStamp,
        autoPlay,
        modalToggle,
        isLoginModalOpen,
        setIsLoginModalOpen,
        currentVideo,
        handleSelection,
        handleLoginSuccess,
        handleEnd,
    };
}
